<script setup lang="ts">
import { ru } from "date-fns/locale";
import VitaButton from "@/components/UI/VitaButton.vue";
import VitaCheckbox from "@/components/UI/VitaCheckbox.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { computed, onMounted, ref } from 'vue';
import {
  addHours,
  addMonths,
  endOfMonth,
  format,
  isBefore,
  isValid,
  parse,
  startOfDay,
  startOfMonth,
  startOfToday,
} from "date-fns";
import store from "@/store";
import { checkUnsavedChanges } from "@/utils/main";
import { Vacation } from "@/types";

interface Props {
  date: string;
  zupGuid: string;
  pharmId: number;
  timezoneOffset: number;
  visible: boolean;
  updateId: string | null;
}

const props = defineProps<Props>();

const onCloseClick = () => {
  confirmCloseModal.value = true;
};

const emit = defineEmits(["update:visible"]);

const closeVacationEdit = () => {
  emit("update:visible", false);
};

const isSingleDay = ref(false);
const confirmCloseModal = ref<boolean>(false);
const vacationDateFrom = ref<Date>(parse(props.date, "d.M.yyyy", new Date()));
const vacationDateTo = ref<Date | null>(null);
const inProcess = ref(false);

// Функция для форматирования отображаемой даты
const getFormattedDate = (date: Date | null, prefix: string) => {
  if (!date || !isValid(date)) return "";
  return `${prefix} ${format(date, "dd.MM.yyyy")}`;
};

// Форматированные даты
const formattedDateFrom = computed(() =>
  isSingleDay.value
    ? getFormattedDate(vacationDateFrom.value, "")
    : getFormattedDate(vacationDateFrom.value, "С")
);

const formattedDateTo = computed(() =>
  isSingleDay.value
    ? getFormattedDate(vacationDateTo.value, "")
    : getFormattedDate(vacationDateTo.value, "По")
);

// Минимальная дата: первое число текущего месяца
const minDate = computed(() => {
  const date = parse(props.date, 'd.M.yyyy', new Date())
  return startOfMonth(date)
})

// Максимальная дата: последнее число следующего месяца
const maxDate = computed(() => {
  const date = parse(props.date, 'd.M.yyyy', new Date())
  return endOfMonth(addMonths(date, 1))
})

// Отключаем прошедшие даты в календаре
const disablePastDates = (date: Date) =>
  isBefore(startOfDay(date), startOfToday());

// Проверка валидности выбранных дат
const isDateFromValid = computed(() => isValid(vacationDateFrom.value));

const isDateToValid = computed(() => isValid(vacationDateTo.value));

const dateFromPlaceholder = computed(() =>
  isSingleDay.value ? "__.__.____" : "С __.__.____"
);

const isPeriodValid = computed(() => {
  if (isSingleDay.value || !vacationDateTo.value) return true;
  return !isBefore(vacationDateTo.value, vacationDateFrom.value);
});

const loader = computed<boolean>(() => {
  return store.getters["user/loader"];
});

const createVacation = async () => {
  if (!vacationDateFrom.value) return;

  // Не пришла вторая дата - устанавливаем на тот же день
  if (!vacationDateTo.value) {
    vacationDateTo.value = vacationDateFrom.value;
  }

  const dateFrom = addHours(
    vacationDateFrom.value,
    props.timezoneOffset
  ).toISOString();
  const dateTo = addHours(
    vacationDateTo.value,
    props.timezoneOffset
  ).toISOString();

  const vacationObj: Vacation = {
    pharmId: props.pharmId,
    zupGuid: props.zupGuid,
    dateFrom,
    dateTo,
  };

  const vacationUpdateObj = {
    pharmId: props.pharmId,
    id: props.updateId,
    zupGuid: props.zupGuid,
    dateFrom,
    dateTo,
  }

  try {
    inProcess.value = true;
    !loader.value ? store.commit("user/setLoader", true) : null;
    let statusCode: number;
    if (props.updateId) {
      statusCode = await store.dispatch("data/updateVacation", vacationUpdateObj);
    } else {
      statusCode = await store.dispatch("data/addVacation", vacationObj);
    }

    if (!isNaN(statusCode) && statusCode >= 400) return false;

    // Закрытие окна
    store.commit("data/closeCreateShift");

    // noinspection ES6MissingAwait
    store.dispatch("data/getFondWorkTime");
    // noinspection ES6MissingAwait
    store.dispatch("data/getFondWorkTimeHuman");
    await store.dispatch("data/getPharmSchedule");
    await store.dispatch("data/getAdminShift");
    // Обновление расписания для незаполненных дней
    await store.dispatch("data/getTimeScheduleDetails");
    await store.dispatch('data/getTimeSchedule')

    checkUnsavedChanges();
  } catch (e) {
    console.error(e);
  } finally {
    inProcess.value = false;
    store.commit("user/setLoader", false);
  }
};

onMounted(() => {
  if (props.updateId) isSingleDay.value = true;
})
</script>

<template>
  <div class="form-container" v-if="visible">
    <p class="shift__title">
      {{updateId ? 'Редактирование НН' : 'Проставление НН'}}
    </p>

    <div class="form-group">
      <span class="form-label">Период</span>
      <div class="form-row">
        <VueDatePicker
          v-model="vacationDateFrom"
          locale="ru"
          :min-date="minDate"
          :max-date="maxDate"
          :format="() => formattedDateFrom"
          :placeholder="dateFromPlaceholder"
          :format-locale="ru"
          :teleport="true"
          :enable-time-picker="false"
          :disabled-dates="disablePastDates"
          :clearable="false"
          :state="isDateFromValid && isPeriodValid"
          :disabled="inProcess"
          prevent-min-max-navigation
          data-qa="vacation-control-datepicker-date-from"
        >
          <template #input-icon></template>
          <template #clear-icon="{ clear }">
            <vita-icon @click="clear" name="clear" data-qa="vacation-control-icon-date-from-clear" />
          </template>
          <template #action-row="{ closePicker, selectDate }">
            <div class="datepicker-buttons">
              <vita-button
                width="inline"
                color="muted"
                class="datepicker-btn"
                @click="closePicker"
                data-qa="vacation-control-button-date-from-cancel"
                >Отмена</vita-button
              >
              <vita-button
                width="inline"
                class="datepicker-btn"
                @click="selectDate"
                data-qa="vacation-control-button-date-from-select"
                >Выбрать</vita-button
              >
            </div>
          </template>
        </VueDatePicker>
        <VueDatePicker
          v-model="vacationDateTo"
          locale="ru"
          :min-date="minDate"
          :max-date="maxDate"
          :format="() => formattedDateTo"
          :placeholder="'По __.__.____'"
          :start-date="parse(props.date, 'd.M.yyyy', new Date())"
          :format-locale="ru"
          :teleport="true"
          :enable-time-picker="false"
          :disabled-dates="disablePastDates"
          :clearable="true"
          prevent-min-max-navigation
          :state="isDateToValid && isPeriodValid"
          :disabled="inProcess"
          v-if="!isSingleDay"
          data-qa="vacation-control-datepicker-date-to"
        >
          <template #input-icon></template>
          <template #clear-icon="{ clear }">
            <vita-icon @click="clear" name="clear" data-qa="vacation-control-icon-date-to-clear" />
          </template>
          <template #action-row="{ closePicker, selectDate }">
            <div class="datepicker-buttons">
              <vita-button
                width="inline"
                color="muted"
                class="datepicker-btn"
                @click="closePicker"
                data-qa="vacation-control-button-date-to-cancel"
                >Отмена</vita-button
              >
              <vita-button
                width="inline"
                class="datepicker-btn"
                @click="selectDate"
                data-qa="vacation-control-button-date-to-select"
                >Выбрать</vita-button
              >
            </div>
          </template>
        </VueDatePicker>
      </div>
      <div
        :class="{ 'date-error': true, 'is-shown': !isPeriodValid }"
      >
        Укажите даты в правильном порядке
      </div>
      <div>
        <VitaCheckbox
          v-model="isSingleDay"
          id="multiply-checkbox"
          label="Проставить НН на один день"
          :disabled="inProcess"
        />
      </div>
    </div>

    <div class="form-row">
      <div>
        <vita-button color="muted" @click="onCloseClick" :disabled="inProcess"
                     data-qa="vacation-control-button-close">
          Отменить
        </vita-button>
        <vita-modal
          title="Вы уверены, что хотите отменить проставление НН?"
          v-if="confirmCloseModal"
        >
          <vita-button color="red" width="inline" @click="closeVacationEdit"
            data-qa="vacation-control-button-close-confirm"
            >Да</vita-button
          >
          <vita-button
            color="blue"
            width="inline"
            @click="confirmCloseModal = false"
            data-qa="vacation-control-button-close-reject"
            >Нет</vita-button
          >
        </vita-modal>
      </div>
      <div>
        <vita-button
          color="blue"
          :disabled="
            inProcess ||
            (!isSingleDay && !isDateToValid) ||
            !isDateFromValid ||
            !isPeriodValid
          "
          @click="createVacation"
          data-qa="vacation-control-button-save"
        >
          Сохранить
        </vita-button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.checkbox-label {
  &:not(:first-child) {
    margin-top: var(--spacing-8);
  }
}

.form-label {
  display: inline-block;
  font-weight: 500;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0;
  color: var(--text-color-secondary);

  &:not(:last-child) {
    margin-bottom: var(--spacing-8);
  }
}

.form-group {
  &:not(:first-child) {
    margin-top: var(--spacing-16);
  }

  &:not(:last-child) {
    margin-bottom: var(--spacing-16);
  }
}

.form-row {
  display: flex;
  gap: var(--spacing-8);

  > div {
    flex: 1;
    max-width: 50%;
  }
}

.date-error {
  color: var(--text-color-danger);
  font-size: 10px;
  margin-bottom: 10px;
  margin-top: 8px;
  visibility: hidden;
  line-height: 1;

  &.is-shown {
    visibility: visible;
  }
}
</style>